const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

// TrainerPhoto.url is a backend-relative path (e.g. "/media/trainer_photos/...") — resolve it
// through the same API base every other request uses, so it works both in dev and behind Caddy.
export function mediaUrl(path: string): string {
  return `${API_URL}${path}`;
}

export class ApiError extends Error {
  status: number;
  detail: unknown;

  constructor(status: number, detail: unknown) {
    super(typeof detail === "string" ? detail : JSON.stringify(detail));
    this.status = status;
    this.detail = detail;
  }
}

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("access_token");
}

export function setToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem("access_token", token);
  else window.localStorage.removeItem("access_token");
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(options.headers);
  // FormData sets its own multipart boundary — forcing json here would break file uploads.
  if (!(options.body instanceof FormData)) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });

  if (res.status === 204) return undefined as T;

  const isJson = res.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    throw new ApiError(res.status, isJson ? body.detail ?? body : body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) => request<T>(path, { method: "POST", body: formData }),
};

// --- Типы (частичное отражение схем backend, только то, что нужно UI) ---

export type UserRole = "student" | "trainer" | "admin";

export interface TokenOut {
  access_token: string;
  token_type: string;
}

export interface TrainerProfile {
  user_id: string;
  display_name: string;
  bio: string | null;
  qualifications: string | null;
  ip_confirmed_at: string | null;
}

export interface TrainerListItem extends TrainerProfile {
  venues: string[];
  min_price: string | null;
  nearest_available_slot: string | null;
}

export interface TrainerPublicPage {
  user_id: string;
  display_name: string;
  bio: string | null;
  qualifications: string | null;
  venues: { id: string; name: string; address: string | null }[];
  prices: { venue_id: string; format: string; amount: string }[];
  package_products: { id: string; session_count: number; total_price: string }[];
  nearest_available_slot: string | null;
  cancellation_rules: string;
  telegram_channel_ids: number[];
}

export interface Venue {
  id: string;
  name: string;
  address: string | null;
  timezone: string;
  description: string | null;
  instructions: string | null;
  contacts: string | null;
}

export interface WorkingInterval {
  id: string;
  trainer_id: string;
  venue_id: string;
  weekday: number;
  start_time: string;
  end_time: string;
}

export interface Slot {
  id: string;
  trainer_id: string;
  venue_id: string;
  start_at: string;
  end_at: string;
  source: "template" | "adhoc";
  manually_closed: boolean;
  status: "available" | "held" | "booked" | "closed_by_trainer" | "archived";
}

export interface Booking {
  id: string;
  slot_id: string;
  student_id: string;
  trainer_id: string;
  status: string;
  cancellation_type: string | null;
  cancellation_reason: string | null;
  recurring_reservation_id: string | null;
  cancel_deadline_at: string;
  reschedule_deadline_at: string;
  created_at: string;
  closed_at: string | null;
  closed_by: string | null;
  slot_start_at: string | null;
  slot_end_at: string | null;
  venue_id: string | null;
  trainer_display_name: string | null;
  student_phone: string | null;
}

export interface Price {
  id: string;
  trainer_id: string;
  venue_id: string;
  format: string;
  amount: string;
  currency: string;
}

export interface PackageProduct {
  id: string;
  trainer_id: string;
  home_venue_id: string;
  format: string;
  session_count: number;
  base_price_per_session: string;
  discount_percent: string;
  total_price: string;
  price_per_session: string;
  status: string;
}

export interface PurchasedPackage {
  id: string;
  package_product_id: string;
  student_id: string;
  purchased_at: string;
  expires_at: string;
  status: string;
  available_units: number;
  session_count: number;
  trainer_id: string;
  price_per_session: string;
}

export interface PurchaseCheckout {
  checkout_url: string;
  provider_payment_id: string;
  hold_id: string;
  hold_expires_at: string;
}

export interface PackagePurchaseCheckout {
  checkout_url: string;
  provider_payment_id: string;
}

export interface DeadlinePreview {
  cancel_deadline_at: string;
  reschedule_deadline_at: string;
}

export interface PackageLedgerEntry {
  id: string;
  type: "reserved" | "consumed" | "released" | "forfeited_late" | "forfeited_no_show" | "annulled_refund";
  booking_id: string | null;
  created_at: string;
}

export interface ExerciseEntry {
  id: string;
  name: string;
  sets: number | null;
  reps: number | null;
  weight: string | null;
  duration_or_distance: string | null;
  actual_performance: string | null;
  revisions: { edited_by: string; edited_at: string; prev: Record<string, unknown>; new: Record<string, unknown> }[];
}

export interface DiarySession {
  program_notes: string | null;
  difficulty: number | null;
  recommendations: string | null;
  closed_by: string | null;
}

export interface Diary {
  session: DiarySession;
  exercises: ExerciseEntry[];
}

export interface ExerciseEntryInput {
  name: string;
  sets?: number | null;
  reps?: number | null;
  weight?: number | null;
  duration_or_distance?: string | null;
  actual_performance?: string | null;
}

export interface HomeworkInput {
  due_date: string;
  exercises: string;
  instructions?: string | null;
  target_metrics?: string | null;
}

export interface DiaryInput {
  program_notes?: string | null;
  exercises?: ExerciseEntryInput[];
  difficulty?: number | null;
  recommendations?: string | null;
  homework?: HomeworkInput | null;
}

export interface Homework {
  id: string;
  trainer_id: string;
  student_id: string;
  due_date: string;
  exercises: string;
  instructions: string | null;
  target_metrics: string | null;
  completion_status: string;
  actual_result: string | null;
}

export interface Measurement {
  id: string;
  metric_type: string;
  value: string | null;
  value_text: string | null;
  unit: string | null;
  measured_at: string;
  source: string;
}

export interface QuestionnaireInput {
  consent_general: boolean;
  consent_special_category: boolean;
  goals?: string | null;
  experience?: string | null;
  activity_level?: string | null;
  height?: number | null;
  weight?: number | null;
  injuries?: string | null;
  conditions?: string | null;
  restrictions?: string | null;
  contraindications?: string | null;
  current_pain?: string | null;
  preferences?: string | null;
  emergency_contact?: string | null;
}

export interface PackageLedgerSummary {
  total: number;
  used: number;
  reserved: number;
  available: number;
  written_off: number;
  entries: PackageLedgerEntry[];
}

export interface TrainerPhoto {
  id: string;
  trainer_id: string;
  kind: "portrait" | "full_body" | "in_gym" | "result";
  url: string;
  status: "pending" | "approved" | "rejected";
  review_note: string | null;
  uploaded_at: string;
}

export interface AdminTrainer {
  user_id: string;
  display_name: string;
  bio: string | null;
  status: "pending" | "approved" | "suspended" | "blocked";
}

export interface AdminRefund {
  id: string;
  payment_id: string;
  amount: string;
  status: string;
}

export interface AdminDebt {
  id: string;
  trainer_id: string;
  debt_type: string;
  amount: string;
  status: string;
}

export interface AdminAuditEvent {
  id: string;
  actor_user_id: string | null;
  action: string;
  entity_type: string;
  created_at: string;
}

export const WEEKDAY_NAMES = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

export const SLOT_STATUS_LABEL: Record<Slot["status"], string> = {
  available: "Свободно",
  held: "Удерживается",
  booked: "Забронировано",
  closed_by_trainer: "Закрыто тренером",
  archived: "Прошло",
};
